import {FormEvent, useState} from 'react'
import {Card, Form, Button, Spinner, Row, Col} from 'react-bootstrap'
import {useQuery, useLazyQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {FormattedMessage, useIntl} from 'react-intl'
import {
  TWILIO_PROVISIONING_STATUS,
  MY_PROVISIONED_NUMBERS,
  TWILIO_AVAILABLE_NUMBERS,
} from '../../gql/queries/twilioNumbers'
import {
  PURCHASE_PROVISIONED_NUMBER,
  RELEASE_PROVISIONED_NUMBER,
  SET_OUTBOUND_CID,
  ENABLE_SMS_ON_NUMBER,
  SET_DEFAULT_MESSAGING_NUMBER,
} from '../../gql/mutations/twilioNumbers'
import {MY_BILLING} from '../../gql/queries/twilioBilling'
import {US_STATES} from '../../helpers/catalogs'
import {AssignNumberModal} from './components/AssignNumberModal'
import {useGetUser} from '../../store/userStore'
import {NUMBERS_MESSAGES as M, EM_DASH} from './numbersMessages'
import {TELEPHONY_MESSAGES as T} from '../../helpers/telephonyMessages'

type ProvisionedNumber = {
  id: string
  e164: string
  areaCode: string
  friendlyName: string
  status: string
  trunkAttached: boolean
  didCreated: boolean
  isOutboundCid: boolean
  capabilities?: {voice?: boolean; sms?: boolean} | null
  smsDefault?: boolean
  created: string
  routeMode?: string
  ownerName?: string
  assignedUserId?: string
  assignedUserName?: string
  assignedGroupId?: string
  assignedGroupName?: string
}

type AvailableNumber = {
  phoneNumber: string
  friendlyName: string
  locality: string
  region: string
  areaCode: string
  voice: boolean
  sms: boolean
}

// +1XXXXXXXXXX -> +1 (XXX) XXX-XXXX
const formatE164 = (e164: string) => {
  const m = e164?.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : e164
}

// The amount is a currency figure, not translatable copy — hoisted out of JSX so
// the `$` + `toFixed` template literal doesn't read as a hardcoded string.
const formatUsd = (value: any) => `$${Number(value).toFixed(2)}`

export const NumbersPage = () => {
  const intl = useIntl()
  const {data: statusData, loading: statusLoading, refetch: refetchStatus} = useQuery(
    TWILIO_PROVISIONING_STATUS,
    {fetchPolicy: 'network-only'}
  )
  const {data: minesData, loading: minesLoading, refetch: refetchMine} = useQuery(
    MY_PROVISIONED_NUMBERS,
    {fetchPolicy: 'network-only'}
  )
  const {data: billingData} = useQuery(MY_BILLING, {fetchPolicy: 'cache-and-network'})
  const billing = billingData?.myBilling

  const status = statusData?.twilioProvisioningStatus
  const enabled = !!status?.enabled
  const remaining: number = status?.remaining ?? 0
  const myNumbers: ProvisionedNumber[] = minesData?.myProvisionedNumbers ?? []
  // The outbound-CID default is per ASSIGNED user (set_outbound_cid requires
  // assigned_user=me), so only offer that control on numbers assigned to me — not on
  // ones I own but assigned to a colleague or a group.
  const currentUserId = useGetUser((s) => s.user?.user?.id) || ''
  const isAssignedToMe = (n: ProvisionedNumber) =>
    !!currentUserId && String(n.assignedUserId || '') === String(currentUserId)

  const [stateCode, setStateCode] = useState('')
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 10
  const [runSearch, {data: searchData, loading: searching, called: searchCalled}] = useLazyQuery(
    TWILIO_AVAILABLE_NUMBERS,
    {fetchPolicy: 'network-only'}
  )
  const ownedSet = new Set(myNumbers.map((n) => n.e164))
  const available: AvailableNumber[] = (searchData?.twilioAvailableNumbers ?? []).filter(
    (a: AvailableNumber) => !ownedSet.has(a.phoneNumber)
  )
  const pageCount = Math.max(1, Math.ceil(available.length / PAGE_SIZE))
  const pageItems = available.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const [purchase, {loading: purchasing}] = useMutation(PURCHASE_PROVISIONED_NUMBER)
  const [release, {loading: releasing}] = useMutation(RELEASE_PROVISIONED_NUMBER)
  const [setOutbound, {loading: settingCid}] = useMutation(SET_OUTBOUND_CID)
  const [enableSms, {loading: enablingSms}] = useMutation(ENABLE_SMS_ON_NUMBER)
  const [setDefaultSms, {loading: settingDefaultSms}] = useMutation(SET_DEFAULT_MESSAGING_NUMBER)
  const [busyKey, setBusyKey] = useState('')
  const [assignTarget, setAssignTarget] = useState<ProvisionedNumber | null>(null)

  const showWarnings = (warnings?: string[]) => (warnings || []).forEach((w) => toast.warning(w))

  const handleSearch = (e?: FormEvent) => {
    e?.preventDefault()
    if (!stateCode) {
      toast.warning(intl.formatMessage(M.toastSelectState))
      return
    }
    setPage(1)
    runSearch({variables: {state: stateCode, country: 'US', limit: 30}}).catch((err: any) =>
      toast.error(err?.message || intl.formatMessage(M.toastSearchFailed))
    )
  }

  const handleBuy = async (num: AvailableNumber) => {
    if (remaining <= 0) return
    setBusyKey(num.phoneNumber)
    try {
      const {data} = await purchase({variables: {phoneNumber: num.phoneNumber}})
      const res = data?.purchaseProvisionedNumber
      if (res?.success) {
        toast.success(
          res.message || intl.formatMessage(M.toastAcquired, {number: num.phoneNumber})
        )
        showWarnings(res.warnings)
        await Promise.all([refetchMine(), refetchStatus()])
      }
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage(M.toastPurchaseFailed))
    } finally {
      setBusyKey('')
    }
  }

  const handleSetOutbound = async (num: ProvisionedNumber) => {
    setBusyKey(num.id)
    try {
      const {data} = await setOutbound({variables: {id: num.id}})
      if (data?.setOutboundCid?.success) {
        toast.success(intl.formatMessage(M.toastOutboundSet, {number: formatE164(num.e164)}))
        await refetchMine()
      }
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage(M.toastOutboundFailed))
    } finally {
      setBusyKey('')
    }
  }

  const handleEnableSms = async (num: ProvisionedNumber) => {
    setBusyKey(num.id)
    try {
      const {data} = await enableSms({variables: {id: num.id}})
      const res = data?.enableSmsOnNumber
      if (res?.success) {
        toast.success(res.message || `SMS enabled on ${formatE164(num.e164)}.`)
        showWarnings(res.warnings)
        await refetchMine()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not enable SMS')
    } finally {
      setBusyKey('')
    }
  }

  const handleSetDefaultSms = async (num: ProvisionedNumber) => {
    setBusyKey(num.id)
    try {
      const {data} = await setDefaultSms({variables: {id: num.id, channelType: 'SMS'}})
      if (data?.setDefaultMessagingNumber?.success) {
        toast.success(`${formatE164(num.e164)} is now the default SMS number.`)
        await refetchMine()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not set default SMS number')
    } finally {
      setBusyKey('')
    }
  }

  const handleRelease = async (num: ProvisionedNumber) => {
    if (!window.confirm(intl.formatMessage(M.confirmRelease, {number: formatE164(num.e164)})))
      return
    setBusyKey(num.id)
    try {
      const {data} = await release({variables: {id: num.id}})
      const res = data?.releaseProvisionedNumber
      if (res?.success) {
        toast.success(res.message || intl.formatMessage(M.toastReleased))
        showWarnings(res.warnings)
        await Promise.all([refetchMine(), refetchStatus()])
      }
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage(M.toastReleaseFailed))
    } finally {
      setBusyKey('')
    }
  }

  if (statusLoading) {
    return (
      <div className='d-flex justify-content-center py-10'>
        <Spinner animation='border' />
      </div>
    )
  }

  if (!enabled) {
    return (
      <Card className='mt-5'>
        <Card.Body className='text-center py-10'>
          <h4 className='mb-3'>{intl.formatMessage(M.disabledTitle)}</h4>
          <p className='text-muted mb-0'>{intl.formatMessage(M.disabledBody)}</p>
        </Card.Body>
      </Card>
    )
  }

  return (
    <div className='mt-5'>
      <div className='d-flex align-items-center justify-content-between mb-5 flex-wrap gap-3'>
        <div>
          <h2 className='mb-1'>{intl.formatMessage(M.title)}</h2>
          <span className='text-muted'>{intl.formatMessage(M.subtitle)}</span>
        </div>
        <span
          className={`badge fs-6 px-4 py-2 ${
            remaining > 0 ? 'badge-light-primary' : 'badge-light-secondary'
          }`}
        >
          {intl.formatMessage(M.usedOf, {
            used: String(status?.activeCount ?? 0),
            max: String(status?.maxNumbers ?? 0),
          })}
        </span>
      </div>

      {billing && (billing.numbers?.length ?? 0) > 0 && (
        <Card className='mb-6'>
          <Card.Body className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
            <div>
              <div className='text-muted fs-7 text-uppercase'>
                {intl.formatMessage(M.chargesTitle)}
              </div>
              <div className='fs-2 fw-bold'>{formatUsd(billing.charge_total)}</div>
              <span className='text-muted fs-8'>{intl.formatMessage(M.chargesBreakdown)}</span>
            </div>
            <div className='text-end'>
              {billing.numbers.map((n: any) => (
                <div key={n.e164} className='fs-8 text-muted'>
                  <FormattedMessage
                    {...M.chargesLine}
                    values={{
                      number: formatE164(n.e164),
                      amount: formatUsd(n.charge_total),
                      calls: n.inbound_calls + n.outbound_calls,
                      b: (chunks) => <span className='fw-semibold'>{chunks}</span>,
                    }}
                  />
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Your numbers */}
      <Card className='mb-6'>
        <Card.Header>
          <Card.Title>{intl.formatMessage(M.mineTitle)}</Card.Title>
        </Card.Header>
        <Card.Body>
          {minesLoading ? (
            <Spinner animation='border' size='sm' />
          ) : myNumbers.length === 0 ? (
            <p className='text-muted mb-0'>{intl.formatMessage(M.mineEmpty)}</p>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed align-middle gy-3'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th>{intl.formatMessage(M.colNumber)}</th>
                    <th>{intl.formatMessage(M.colArea)}</th>
                    <th>{intl.formatMessage(M.colAssignedTo)}</th>
                    <th>{intl.formatMessage(M.colRouting)}</th>
                    <th>{intl.formatMessage(M.colOutboundCid)}</th>
                    <th className='text-end'>{intl.formatMessage(M.colActions)}</th>
                  </tr>
                </thead>
                <tbody>
                  {myNumbers.map((n) => (
                    <tr key={n.id}>
                      <td className='fw-bold'>{formatE164(n.e164)}</td>
                      <td>{n.areaCode || EM_DASH}</td>
                      <td>
                        {n.assignedGroupName ? (
                          <span className='badge badge-light-info'>
                            {intl.formatMessage(M.assignedGroup, {name: n.assignedGroupName})}
                          </span>
                        ) : n.assignedUserName ? (
                          <span className='badge badge-light-primary'>{n.assignedUserName}</span>
                        ) : (
                          <span className='text-muted'>{EM_DASH}</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge me-2 ${
                            n.trunkAttached ? 'badge-light-success' : 'badge-light-warning'
                          }`}
                        >
                          {intl.formatMessage(n.trunkAttached ? M.trunkReady : M.trunkPending)}
                        </span>
                        <span
                          className={`badge ${
                            n.didCreated ? 'badge-light-success' : 'badge-light-warning'
                          }`}
                        >
                          {intl.formatMessage(n.didCreated ? M.inboundReady : M.inboundPending)}
                        </span>
                      </td>
                      <td>
                        {isAssignedToMe(n) ? (
                          n.isOutboundCid ? (
                            <span className='badge badge-light-success'>
                              {intl.formatMessage(M.outboundCurrent)}
                            </span>
                          ) : (
                            <Button
                              variant='light-primary'
                              size='sm'
                              disabled={settingCid && busyKey === n.id}
                              onClick={() => handleSetOutbound(n)}
                            >
                              {settingCid && busyKey === n.id ? (
                                <Spinner animation='border' size='sm' />
                              ) : (
                                intl.formatMessage(M.outboundUse)
                              )}
                            </Button>
                          )
                        ) : (
                          <span className='text-muted'>{EM_DASH}</span>
                        )}
                      </td>
                      <td className='text-end'>
                        {!n.capabilities?.sms ? (
                          <Button
                            variant='light-success'
                            size='sm'
                            className='me-2'
                            disabled={enablingSms && busyKey === n.id}
                            onClick={() => handleEnableSms(n)}
                          >
                            {enablingSms && busyKey === n.id ? (
                              <Spinner animation='border' size='sm' />
                            ) : (
                              intl.formatMessage(M.smsEnable)
                            )}
                          </Button>
                        ) : n.smsDefault ? (
                          <span className='badge badge-light-success me-2'>
                            {intl.formatMessage(M.smsIsDefault)}
                          </span>
                        ) : (
                          <>
                            <span className='badge badge-light-success me-2'>
                              {intl.formatMessage(M.smsReady)}
                            </span>
                            <Button
                              variant='light-primary'
                              size='sm'
                              className='me-2'
                              disabled={settingDefaultSms && busyKey === n.id}
                              onClick={() => handleSetDefaultSms(n)}
                            >
                              {settingDefaultSms && busyKey === n.id ? (
                                <Spinner animation='border' size='sm' />
                              ) : (
                                intl.formatMessage(M.smsSetDefault)
                              )}
                            </Button>
                          </>
                        )}
                        <Button
                          variant='light-primary'
                          size='sm'
                          className='me-2'
                          onClick={() => setAssignTarget(n)}
                        >
                          {intl.formatMessage(T.assign)}
                        </Button>
                        <Button
                          variant='light-danger'
                          size='sm'
                          disabled={releasing && busyKey === n.id}
                          onClick={() => handleRelease(n)}
                        >
                          {releasing && busyKey === n.id ? (
                            <Spinner animation='border' size='sm' />
                          ) : (
                            intl.formatMessage(T.release)
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Get a new number */}
      <Card>
        <Card.Header>
          <Card.Title>{intl.formatMessage(M.searchTitle)}</Card.Title>
        </Card.Header>
        <Card.Body>
          {remaining <= 0 && (
            <div className='alert alert-warning'>
              {intl.formatMessage(M.limitReached, {max: Number(status?.maxNumbers ?? 0)})}
            </div>
          )}
          <Form onSubmit={handleSearch} className='mb-5'>
            <Row className='g-3 align-items-end'>
              <Col xs='auto'>
                <Form.Label>{intl.formatMessage(M.state)}</Form.Label>
                <Form.Select
                  style={{minWidth: 240}}
                  value={stateCode}
                  onChange={(e: any) => setStateCode(e.target.value)}
                >
                  <option value=''>{intl.formatMessage(T.selectState)}</option>
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs='auto'>
                <Button type='submit' variant='primary' disabled={searching}>
                  {searching ? (
                    <>
                      <Spinner animation='border' size='sm' className='me-2' />
                      {intl.formatMessage(M.searching)}
                    </>
                  ) : (
                    intl.formatMessage(M.search)
                  )}
                </Button>
              </Col>
            </Row>
          </Form>

          {searchCalled && !searching && (
            available.length === 0 ? (
              <p className='text-muted mb-0'>{intl.formatMessage(M.searchEmpty)}</p>
            ) : (
              <div className='table-responsive'>
                <table className='table table-row-dashed align-middle gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th>{intl.formatMessage(M.colNumber)}</th>
                      <th>{intl.formatMessage(M.colLocation)}</th>
                      <th className='text-end'>{intl.formatMessage(M.colAction)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((a) => (
                      <tr key={a.phoneNumber}>
                        <td className='fw-bold'>{formatE164(a.phoneNumber)}</td>
                        <td>{[a.locality, a.region].filter(Boolean).join(', ') || EM_DASH}</td>
                        <td className='text-end'>
                          <Button
                            variant='primary'
                            size='sm'
                            disabled={remaining <= 0 || (purchasing && busyKey === a.phoneNumber)}
                            onClick={() => handleBuy(a)}
                          >
                            {purchasing && busyKey === a.phoneNumber ? (
                              <Spinner animation='border' size='sm' />
                            ) : (
                              intl.formatMessage(T.buy)
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pageCount > 1 && (
                  <div className='d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2'>
                    <span className='text-muted'>
                      {intl.formatMessage(M.showingRange, {
                        start: String((page - 1) * PAGE_SIZE + 1),
                        end: String(Math.min(page * PAGE_SIZE, available.length)),
                        total: String(available.length),
                      })}
                    </span>
                    <div className='d-flex gap-2'>
                      <Button
                        variant='light'
                        size='sm'
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        {intl.formatMessage(M.previous)}
                      </Button>
                      <span className='align-self-center text-muted'>
                        {intl.formatMessage(M.pageIndicator, {
                          page: String(page),
                          total: String(pageCount),
                        })}
                      </span>
                      <Button
                        variant='light'
                        size='sm'
                        disabled={page >= pageCount}
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                      >
                        {intl.formatMessage(M.next)}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </Card.Body>
      </Card>

      <AssignNumberModal
        show={!!assignTarget}
        onHide={() => setAssignTarget(null)}
        numberId={assignTarget?.id ?? null}
        numberLabel={assignTarget ? formatE164(assignTarget.e164) : ''}
        currentAssignee={
          assignTarget?.assignedGroupName
            ? intl.formatMessage(M.assignedGroup, {name: assignTarget.assignedGroupName})
            : assignTarget?.assignedUserName || ''
        }
        onAssigned={() => refetchMine()}
      />
    </div>
  )
}
